import { z } from 'zod';

import { BlockValueStringSchema } from './blockValueStringSchema.js';

interface BlockValueUuidSchemaArgs {
  isRequired?: boolean;
}

function createBlockValueUuidInnerSchema() {
  return BlockValueStringSchema({
    stringValidationSchema: z.string().uuid({ message: 'Must be a valid ID.' }),
  });
}

type BlockValueUuidInnerSchema = ReturnType<typeof createBlockValueUuidInnerSchema>;

type BlockValueUuidOptionalEmptySchema = z.ZodType<string | null, string>;

/**
 * @arg isRequired - if isRequired is false, the empty string is accepted as input and coerced to null.
 * Chain the schema with .optional() if the entire config property is optional.
 * @returns A Zod schema that validates block value templates or UUIDs. When isRequired is false, it also
 * accepts the empty string and coerces it to null.
 */
export function BlockValueUuidSchema(args: { isRequired: true }): BlockValueUuidInnerSchema;
export function BlockValueUuidSchema(args?: {
  isRequired?: false;
}): BlockValueUuidOptionalEmptySchema;
export function BlockValueUuidSchema(args: {
  isRequired: boolean;
}): BlockValueUuidInnerSchema | BlockValueUuidOptionalEmptySchema;
export function BlockValueUuidSchema(args: BlockValueUuidSchemaArgs = {}) {
  const { isRequired = false } = args;
  const blockValueOrUuidSchema = createBlockValueUuidInnerSchema();
  if (isRequired) {
    return blockValueOrUuidSchema;
  }
  return z
    .union([z.literal(''), blockValueOrUuidSchema])
    .transform((value) => (value.length === 0 ? null : value)) as BlockValueUuidOptionalEmptySchema;
}
